import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Save, FolderOpen, FileText, Copy, ClipboardPaste, Scissors } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import {
  Menubar,
  MenubarContent,
  MenubarItem,
  MenubarMenu,
  MenubarSeparator,
  MenubarTrigger,
} from '../ui/menubar';
import { SaveAsDialog } from './SaveAsDialog';
import { globalNavigator } from '../WindowContent';

interface NotepadProps {
  filePath?: string;
  initialContent?: string;
  onSave?: (content: string, filePath: string) => void;
  onOpenWindow?: (appId: string, title: string, filePath?: string, content?: string) => void;
}

export const Notepad: React.FC<NotepadProps> = ({ 
  filePath = '', 
  initialContent = '', 
  onSave,
  onOpenWindow
}) => {
  const [content, setContent] = useState(initialContent);
  const [fileName, setFileName] = useState(filePath ? filePath.split('\\').pop() || 'Untitled' : 'Untitled');
  const [isModified, setIsModified] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    setContent(initialContent);
    setIsModified(false);
    if (filePath) {
      setFileName(filePath.split('\\').pop() || 'Untitled');
    }
  }, [filePath, initialContent]);

  useEffect(() => {
    const loadFromVFS = async () => {
      if (!filePath) return;
      if (content && content.length > 0) return;

      await globalNavigator.waitForInitialization();
      await globalNavigator.syncWithVFS();
      const item = globalNavigator.findItemByPath(filePath);
      if (item?.content !== undefined) {
        setContent(item.content);
        setIsModified(false);
      }
    };
    loadFromVFS();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filePath]);

  const handleContentChange = (value: string) => {
    setContent(value);
    setIsModified(true);
  };

  const handleSave = () => {
    if (filePath && onSave) {
      onSave(content, filePath);
      setIsModified(false);
      toast({
        title: "File saved",
        description: `${fileName} has been saved successfully.`,
      });
    } else {
      setShowSaveDialog(true);
    }
  };

  const handleSaveAs = () => {
    setShowSaveDialog(true);
  };

  const handleSaveAsComplete = (path: string, fileName: string) => {
    if (onSave) {
      onSave(content, path);
      setIsModified(false);
      setShowSaveDialog(false);
      setFileName(fileName);
      toast({
        title: "File saved",
        description: `${fileName} has been saved successfully.`,
      });
    }
  };

  const handleNewFile = () => {
    if (isModified) {
      const shouldProceed = confirm("You have unsaved changes. Do you want to create a new file anyway?");
      if (!shouldProceed) return;
    }
    setContent('');
    setFileName('Untitled');
    setIsModified(false);
  };

  const handleOpenFile = () => {
    if (onOpenWindow) {
      onOpenWindow('file-explorer', 'File Explorer');
    }
  };

  const handleCopy = () => {
    const textarea = document.querySelector('textarea');
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const selectedText = content.substring(start, end);
      if (selectedText) {
        navigator.clipboard.writeText(selectedText);
        toast({
          title: "Text copied",
          description: "Selected text copied to clipboard.",
        });
      }
    }
  };

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      const textarea = document.querySelector('textarea') as HTMLTextAreaElement;
      if (textarea) {
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const newContent = content.substring(0, start) + text + content.substring(end);
        setContent(newContent);
        setIsModified(true);
      }
    } catch (err) {
      toast({
        title: "Paste failed",
        description: "Unable to paste from clipboard.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="h-full flex flex-col bg-background overflow-hidden">
      {/* Menu Bar */}
      <div className="border-b border-border bg-muted/50">
        <Menubar className="h-9 rounded-none border-none bg-transparent px-1">
          <MenubarMenu>
            <MenubarTrigger className="h-8 px-3 py-1 text-xs">File</MenubarTrigger>
            <MenubarContent className="z-[10000]">
              <MenubarItem onClick={handleNewFile}>New</MenubarItem>
              <MenubarItem onClick={handleOpenFile}>Open...</MenubarItem>
              <MenubarSeparator />
              <MenubarItem onClick={handleSave} disabled={!isModified && !filePath}>Save</MenubarItem>
              <MenubarItem onClick={handleSaveAs}>Save As...</MenubarItem>
              <MenubarSeparator />
              <MenubarItem>Print...</MenubarItem>
              <MenubarSeparator />
              <MenubarItem>Exit</MenubarItem>
            </MenubarContent>
          </MenubarMenu>

          <MenubarMenu>
            <MenubarTrigger className="h-8 px-3 py-1 text-xs">Edit</MenubarTrigger>
            <MenubarContent className="z-[10000]">
              <MenubarItem>Undo</MenubarItem>
              <MenubarSeparator />
              <MenubarItem>Cut</MenubarItem>
              <MenubarItem onClick={handleCopy}>Copy</MenubarItem>
              <MenubarItem onClick={handlePaste}>Paste</MenubarItem>
              <MenubarItem>Delete</MenubarItem>
              <MenubarSeparator />
              <MenubarItem>Select All</MenubarItem>
              <MenubarItem>Time/Date</MenubarItem>
            </MenubarContent>
          </MenubarMenu>

          <MenubarMenu>
            <MenubarTrigger className="h-8 px-3 py-1 text-xs">Format</MenubarTrigger>
            <MenubarContent className="z-[10000]">
              <MenubarItem>Word Wrap</MenubarItem>
              <MenubarItem>Font...</MenubarItem>
            </MenubarContent>
          </MenubarMenu>

          <MenubarMenu>
            <MenubarTrigger className="h-8 px-3 py-1 text-xs">View</MenubarTrigger>
            <MenubarContent className="z-[10000]">
              <MenubarItem>Status Bar</MenubarItem>
            </MenubarContent>
          </MenubarMenu>

          <MenubarMenu>
            <MenubarTrigger className="h-8 px-3 py-1 text-xs">Help</MenubarTrigger>
            <MenubarContent className="z-[10000]">
              <MenubarItem>Help Topics</MenubarItem>
              <MenubarSeparator />
              <MenubarItem>About Notepad</MenubarItem>
            </MenubarContent>
          </MenubarMenu>
        </Menubar>
      </div>

      {/* Toolbar */}
      <div className="border-b border-border p-2 bg-muted/30">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={handleSave} className="gap-1">
            <Save size={14} />
            Save
          </Button>
          <Button variant="ghost" size="sm" onClick={handleSaveAs} className="gap-1">
            <FolderOpen size={14} />
            Save As
          </Button>
          <div className="w-px h-6 bg-border mx-1" />
          <Button variant="ghost" size="sm" onClick={handleCopy} className="gap-1">
            <Copy size={14} />
            Copy
          </Button>
          <Button variant="ghost" size="sm" onClick={handlePaste} className="gap-1">
            <ClipboardPaste size={14} />
            Paste
          </Button>
          <Button variant="ghost" size="sm" className="gap-1">
            <Scissors size={14} />
            Cut
          </Button>
        </div>
      </div>

      {/* Editor */}
      <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-2 border-b border-border bg-muted/20">
          <FileText size={16} className="text-blue-600" />
          <span className="text-sm font-medium">
            {fileName}{isModified ? '*' : ''}
          </span>
          {filePath && (
            <span className="text-xs text-muted-foreground">
              - {filePath}
            </span>
          )}
        </div>
        
        <div className="flex-1 min-h-0">
          <Textarea
            value={content}
            onChange={(e) => handleContentChange(e.target.value)}
            className="h-full min-h-0 resize-none border-0 rounded-none focus-visible:ring-0 font-mono text-sm p-4 bg-background"
            placeholder="Start typing..."
          />
        </div>
      </div>

      {showSaveDialog && (
        <SaveAsDialog
          isOpen={showSaveDialog}
          onClose={() => setShowSaveDialog(false)}
          onSave={handleSaveAsComplete}
          currentFileName={fileName}
        />
      )}

    </div>
  );
};